import { boardingCheck } from '../domain/boarding-check.js';
export type AudioPoint = { x: number; y: number };
export type AudioDesk = AudioPoint & { id: number; index: number; unlocked: boolean; workers: number; served: number; boardingSerial?: number };
export type AudioActor = AudioPoint & { id: number; activity: string; volume?: number };
export type ActivityCue = { sound: string; position: AudioPoint; volume: number; sourceId?: number; delay?: number };
const DESK_WORK = ['apt-agent-typing-short', 'apt-suitcase-zip', 'apt-xray-belt', 'apt-agent-typing-short', 'apt-card-tap', 'apt-radio-key', '', 'apt-radio-key'];
const TYPING_SOUNDS = ['apt-agent-typing-short', 'apt-agent-typing-long', 'apt-agent-typing-frantic'];
const TYPING_FRAMES = [108, 276, 180];
const DESK_DONE = ['apt-scan-ok', 'apt-bag-thud', 'apt-tray-stack', 'apt-passport-stamp', 'apt-shop-till', 'apt-radio-key', 'apt-scan-ok', 'apt-radio-key'];

let audioRandomState = 0x9e3779b9;
/** Deterministic stand-in for Math.random: source-guarded code must not desync lockstep peers. */
const audioRandom = (): number => {
  audioRandomState = (audioRandomState * 1664525 + 1013904223) >>> 0;
  return audioRandomState / 0x100000000;
};

/** Local audible activity, bounded per source so work sounds do not stack each frame. */
export class AirportActivityAudio {
  private desks = new Map<number, { served: number; workAt: number; typingIndex: number; boardingSerial: number }>();
  private actors = new Map<number, { activity: string; nextAt: number }>();
  constructor(private random: () => number = audioRandom) {}
  poll(frame: number, desks: AudioDesk[], actors: AudioActor[]): ActivityCue[] {
    const cues: ActivityCue[] = [], activeDesks = new Set<number>(), activeActors = new Set<number>();
    for (const desk of desks) {
      if (!desk.unlocked) continue;
      activeDesks.add(desk.id);
      const previous = this.desks.get(desk.id) ?? { served: desk.served, workAt: frame, typingIndex: -1, boardingSerial: -1 };
      if (desk.index === 6 && desk.workers > 0 && desk.boardingSerial !== undefined && desk.boardingSerial !== previous.boardingSerial) {
        const check = boardingCheck(desk.boardingSerial);
        if (check.kind === 'retry') cues.push({ sound: 'apt-scan-fail', position: desk, volume: 1.1 });
        else if (check.kind === 'keyboard') for (const delay of [0, 0.1, 0.21]) cues.push({ sound: 'apt-key-solid', position: desk, volume: 0.8, delay });
        previous.boardingSerial = desk.boardingSerial;
      }
      if (desk.index !== 6 && desk.workers > 0 && frame >= previous.workAt) {
        if (DESK_WORK[desk.index] === 'apt-agent-typing-short') {
          let typingIndex = Math.floor(this.random() * (previous.typingIndex < 0 ? 3 : 2));
          if (previous.typingIndex >= 0 && typingIndex >= previous.typingIndex) typingIndex++;
          cues.push({ sound: TYPING_SOUNDS[typingIndex], position: desk, volume: 1 + this.random() * 0.25, sourceId: desk.id });
          // Leave a thinking pause after the whole burst rather than layering long recordings.
          previous.workAt = frame + TYPING_FRAMES[typingIndex] + 45 + Math.floor(this.random() * 106);
          previous.typingIndex = typingIndex;
        } else {
          cues.push({ sound: DESK_WORK[desk.index], position: desk, volume: 1.25 });
          previous.workAt = frame + (desk.index === 2 ? 250 : 120);
        }
      }
      if (desk.workers === 0) previous.workAt = frame;
      if (desk.served > previous.served) {
        if (desk.index === 6) {
          cues.push({ sound: 'apt-scan-ok', position: desk, volume: 1.4 });
        } else cues.push({ sound: DESK_DONE[desk.index], position: desk, volume: 1.4 });
        if (desk.index === 3) cues.push({ sound: 'apt-scan-ok', position: desk, volume: 1.1 });
      }
      previous.served = desk.served;
      this.desks.set(desk.id, previous);
    }
    for (const actor of actors) {
      if (!actor.activity) continue;
      activeActors.add(actor.id);
      const previous = this.actors.get(actor.id);
      if (!previous || previous.activity !== actor.activity || frame >= previous.nextAt) {
        cues.push({ sound: actor.activity, position: actor, volume: actor.volume ?? 1.2 });
        this.actors.set(actor.id, { activity: actor.activity, nextAt: frame + (actor.activity === 'apt-footstep-tile' ? 32 : 180) });
      }
    }
    for (const id of this.desks.keys()) if (!activeDesks.has(id)) this.desks.delete(id);
    for (const id of this.actors.keys()) if (!activeActors.has(id)) this.actors.delete(id);
    return cues;
  }
  clear(): void { this.desks.clear(); this.actors.clear(); }
}
